'use client';

import React, { useState } from 'react';
import type { Step2Result, CrossRefResult, EmotionScore } from '../utils/step2Scoring';
import { CONCLUSION_CONFIG } from '../utils/step2Scoring';
import type { TraderProfile } from '../utils/numerology';

interface Step2ReportProps {
  result: Step2Result;
  step1Result: TraderProfile;
  onBack: () => void;
  onRestartStep1: () => void;
}

const OPEN_Q_LABELS: Record<number, string> = {
  1: 'Biggest Trading Problem',
  4: 'Recent Trade That Went Wrong',
  5: 'Repeating Pattern in Worst Trades',
  6: 'Most Frequently Broken Rule',
  17: 'Hardest Thing About Consistent Profitability',
  18: 'One Thing to Fix in Trading Behaviour',
};

const EMOTION_ICONS: Record<string, string> = {
  Fear: '😰',
  Greed: '💰',
  Ego: '🎭',
  Impatience: '⚡',
  Hope: '🌫️',
};

const CONCLUSION_ICONS: Record<string, string> = {
  CONFIRMED: '🔴',
  DORMANT: '🟡',
  SITUATIONAL: '🔵',
  CLEAR: '🟢',
};

const CONCLUSION_COLORS: Record<string, { bg: string; border: string; badge: string; text: string }> = {
  CONFIRMED: { bg: 'bg-red-500/10', border: 'border-red-500/30', badge: 'bg-red-500/20 text-red-300', text: 'text-red-400' },
  DORMANT: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', badge: 'bg-amber-500/20 text-amber-300', text: 'text-amber-400' },
  SITUATIONAL: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', badge: 'bg-blue-500/20 text-blue-300', text: 'text-blue-400' },
  CLEAR: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', badge: 'bg-emerald-500/20 text-emerald-300', text: 'text-emerald-400' },
};

const LEVEL_COLORS: Record<string, string> = {
  Strong: 'text-red-400',
  Moderate: 'text-amber-400',
  Low: 'text-emerald-400',
};

const LEVEL_BAR_COLORS: Record<string, string> = {
  Strong: 'bg-red-500',
  Moderate: 'bg-amber-500',
  Low: 'bg-emerald-500',
};

export default function Step2Report({ result, step1Result, onBack, onRestartStep1 }: Step2ReportProps) {
  const [activeTab, setActiveTab] = useState<'crossref' | 'scores' | 'openanswers' | 'summary'>('crossref');

  const tabs = [
    { id: 'crossref', label: 'Cross-Reference', emoji: '🔗' },
    { id: 'scores', label: 'Emotion Scores', emoji: '📊' },
    { id: 'openanswers', label: 'Open Answers', emoji: '📝' },
    { id: 'summary', label: 'Summary', emoji: '📋' },
  ] as const;

  // Count conclusions
  const confirmed = result.crossRef.filter(r => r.conclusion === 'CONFIRMED').length;
  const situational = result.crossRef.filter(r => r.conclusion === 'SITUATIONAL').length;
  const dormant = result.crossRef.filter(r => r.conclusion === 'DORMANT').length;
  const clear = result.crossRef.filter(r => r.conclusion === 'CLEAR').length;

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="rounded-2xl p-6 bg-blue-500/10 border border-blue-500/30">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs font-medium mb-2">
              <span>🧠</span>
              <span>Step 2 — Behavioural Assessment Complete</span>
            </div>
            <h2 className="text-2xl font-bold text-white">{result.traderName}</h2>
            <p className="text-slate-400 text-sm mt-0.5">DOB: {result.traderDob} · Completed: {new Date(result.completedAt).toLocaleDateString()}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {confirmed > 0 && (
              <span className="px-3 py-1 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 text-xs font-semibold">
                🔴 {confirmed} Confirmed
              </span>
            )}
            {situational > 0 && (
              <span className="px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs font-semibold">
                🔵 {situational} Situational
              </span>
            )}
            {dormant > 0 && (
              <span className="px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-semibold">
                🟡 {dormant} Dormant
              </span>
            )}
            {clear > 0 && (
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-semibold">
                🟢 {clear} Clear
              </span>
            )}
          </div>
        </div>

        {/* Awareness level */}
        <div className="mt-4 p-3 rounded-xl bg-slate-900/50 border border-slate-700/40">
          <p className="text-slate-500 text-xs uppercase tracking-widest mb-1">Q16 — Self-Awareness Level</p>
          <p className="text-slate-200 text-sm">{result.awarenessLevel}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white' :'bg-slate-800/60 text-slate-400 hover:text-slate-200 hover:bg-slate-700/60'
            }`}
          >
            <span>{tab.emoji}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'crossref' && <CrossRefTab crossRef={result.crossRef} />}
      {activeTab === 'scores' && <ScoresTab scores={result.emotionScores} />}
      {activeTab === 'openanswers' && <OpenAnswersTab result={result} />}
      {activeTab === 'summary' && <SummaryTab result={result} step1Result={step1Result} />}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2">
        <button
          onClick={onBack}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-slate-800/60 border border-slate-700/50 text-slate-300 text-sm font-medium hover:bg-slate-700/60 transition-all"
        >
          <span>←</span>
          <span>Back to Step 1 Results</span>
        </button>
        <div className="flex gap-3 sm:ml-auto">
          <button
            onClick={onRestartStep1}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-slate-800/60 border border-slate-700/50 text-slate-300 text-sm font-medium hover:bg-slate-700/60 transition-all"
          >
            <span>↺</span>
            <span>New Assessment</span>
          </button>
        </div>
      </div>

      {/* Step 3 Reminder */}
      <div className="rounded-2xl border border-slate-700/50 bg-slate-900/60 p-5">
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center text-purple-300 text-sm font-bold flex-shrink-0">3</div>
          <div>
            <p className="text-slate-200 text-sm font-semibold">Step 3 — Practitioner Cross-Check</p>
            <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">
              A certified practitioner reviews both Step 1 (I Ching inborn pattern) and Step 2 (behavioural assessment) outputs to finalise the personalised correction plan and coaching roadmap.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Cross-Reference Tab ───────────────────────────────────────────────────────
function CrossRefTab({ crossRef }: { crossRef: CrossRefResult[] }) {
  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {(['CONFIRMED', 'DORMANT', 'SITUATIONAL', 'CLEAR'] as const).map(c => {
          const col = CONCLUSION_COLORS[c];
          const config = CONCLUSION_CONFIG[c];
          return (
            <div key={c} className={`rounded-xl p-3 border ${col.bg} ${col.border}`}>
              <p className={`text-xs font-bold mb-1 ${col.text}`}>{CONCLUSION_ICONS[c]} {c}</p>
              <p className="text-slate-500 text-xs leading-relaxed">{config.detail.split('.')[0]}.</p>
            </div>
          );
        })}
      </div>

      {/* Results */}
      <div className="space-y-3">
        {crossRef.map(ref => {
          const col = CONCLUSION_COLORS[ref.conclusion];
          return (
            <div key={`ref-${ref.emotion}`} className={`rounded-2xl p-5 border ${col.bg} ${col.border}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{EMOTION_ICONS[ref.emotion] ?? '•'}</span>
                  <div>
                    <p className="text-white font-bold text-base">{ref.emotion}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${col.badge}`}>
                        {CONCLUSION_ICONS[ref.conclusion]} {ref.conclusion}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 text-xs">
                  <div className="text-center">
                    <p className="text-slate-500 mb-0.5">I Ching (Step 1)</p>
                    <p className={ref.iChingFound ? 'text-red-400 font-semibold' : 'text-emerald-400 font-semibold'}>
                      {ref.iChingFound ? '✓ Found' : '✗ Not Found'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-slate-500 mb-0.5">Questionnaire (Step 2)</p>
                    <p className={ref.questionnaireActive ? 'text-red-400 font-semibold' : 'text-emerald-400 font-semibold'}>
                      {ref.questionnaireActive ? `✓ Active (${ref.score.percentage}%)` : `✗ Low (${ref.score.percentage}%)`}
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">{ref.conclusionDetail}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Scores Tab ────────────────────────────────────────────────────────────────
function ScoresTab({ scores }: { scores: EmotionScore[] }) {
  return (
    <div className="space-y-4">
      <div className="dimension-card rounded-2xl p-6">
        <p className="text-slate-500 text-xs uppercase tracking-widest mb-1">Emotion Signal Scores</p>
        <p className="text-slate-600 text-xs mb-5">
          Derived from multiple-choice answers only. Open-text answers are not scored — they are reviewed qualitatively by the practitioner.
        </p>
        <div className="space-y-5">
          {scores.map(score => (
            <div key={`score-${score.emotion}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span>{EMOTION_ICONS[score.emotion] ?? '•'}</span>
                  <span className="text-slate-200 text-sm font-medium">{score.emotion}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    score.level === 'Strong' ? 'bg-red-500/20 text-red-300' :
                    score.level === 'Moderate'? 'bg-amber-500/20 text-amber-300' : 'bg-emerald-500/20 text-emerald-300'
                  }`}>
                    {score.level}
                  </span>
                  <span className={`text-sm font-bold ${LEVEL_COLORS[score.level]}`}>{score.percentage}%</span>
                </div>
              </div>
              <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${LEVEL_BAR_COLORS[score.level]}`}
                  style={{ width: `${score.percentage}%` }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-600 text-xs">Signals: {score.signals}/{score.maxSignals}</span>
                <div className="flex gap-3 text-xs text-slate-600">
                  <span className="text-emerald-600">0–34% Low</span>
                  <span className="text-amber-600">35–59% Moderate</span>
                  <span className="text-red-600">60%+ Strong</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl p-4 bg-slate-800/40 border border-slate-700/40">
        <p className="text-slate-500 text-xs leading-relaxed">
          <span className="text-slate-300 font-medium">Note for practitioner:</span> The trader never sees which emotion each answer maps to. These scores reflect behavioural signals only — they must be cross-referenced with the Step 1 I Ching inborn pattern before drawing any conclusion.
        </p>
      </div>
    </div>
  );
}

// ─── Open Answers Tab ─────────────────────────────────────────────────────────
function OpenAnswersTab({ result }: { result: Step2Result }) {
  const openQIds = [1, 4, 5, 6, 17, 18];

  return (
    <div className="space-y-4">
      <div className="rounded-xl p-4 bg-slate-800/40 border border-slate-700/40">
        <p className="text-slate-400 text-xs leading-relaxed">
          <span className="text-slate-200 font-medium">Practitioner view:</span> These are the trader's raw, unfiltered answers. They reveal the trader's self-perception and language patterns — review alongside the scored results for the most complete picture.
        </p>
      </div>

      {/* Open text answers */}
      {openQIds.map(qId => {
        const answer = result.openAnswers[qId];
        if (!answer) return null;
        return (
          <div key={`open-${qId}`} className="dimension-card rounded-2xl p-5">
            <p className="text-slate-500 text-xs uppercase tracking-widest mb-1">Q{qId} — {OPEN_Q_LABELS[qId]}</p>
            <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">{answer}</p>
          </div>
        );
      })}

      {/* Select + Scale answers */}
      <div className="dimension-card rounded-2xl p-5">
        <p className="text-slate-500 text-xs uppercase tracking-widest mb-3">Background Information</p>
        <div className="space-y-3">
          {result.selectAnswers[2] && (
            <div className="flex justify-between items-center py-2 border-b border-slate-800">
              <p className="text-slate-400 text-sm">Years Trading</p>
              <p className="text-slate-200 text-sm font-medium">{result.selectAnswers[2]}</p>
            </div>
          )}
          {result.scaleAnswers[3] !== undefined && (
            <div className="flex justify-between items-center py-2">
              <p className="text-slate-400 text-sm">Self-Rated Consistency</p>
              <p className="text-slate-200 text-sm font-medium">{result.scaleAnswers[3]}/10</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Summary Tab ──────────────────────────────────────────────────────────────
function SummaryTab({ result, step1Result }: { result: Step2Result; step1Result: TraderProfile }) {
  const confirmed = result.crossRef.filter(r => r.conclusion === 'CONFIRMED');
  const situational = result.crossRef.filter(r => r.conclusion === 'SITUATIONAL');
  const dormant = result.crossRef.filter(r => r.conclusion === 'DORMANT');

  return (
    <div className="space-y-5">
      {/* Step 1 Summary */}
      <div className="dimension-card rounded-2xl p-5">
        <p className="text-slate-500 text-xs uppercase tracking-widest mb-3">Step 1 — I Ching Pattern Summary</p>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-lg">☯</div>
          <div>
            <p className="text-white font-semibold">{step1Result.archetype} — {step1Result.traderType}</p>
            <p className="text-slate-400 text-xs">Main Character Number: {step1Result.mainCharacterNumber} · Category {step1Result.category.category} — {step1Result.category.label}</p>
          </div>
        </div>
        {step1Result.emotions.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {step1Result.emotions.map(em => (
              <span key={`s1em-${em.emotion}`} className="px-2.5 py-1 rounded-full bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs">
                {em.emotion}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-emerald-400 text-xs">No dominant bias patterns in I Ching design</p>
        )}
      </div>

      {/* Step 2 Summary */}
      <div className="dimension-card rounded-2xl p-5">
        <p className="text-slate-500 text-xs uppercase tracking-widest mb-3">Step 2 — Behavioural Assessment Summary</p>
        <p className="text-slate-400 text-xs mb-3">Self-Awareness: <span className="text-slate-200">{result.awarenessLevel}</span></p>
        <div className="space-y-2">
          {result.emotionScores.filter(s => s.level !== 'Low').map(s => (
            <div key={`s2em-${s.emotion}`} className="flex items-center justify-between py-1.5 border-b border-slate-800/60">
              <div className="flex items-center gap-2">
                <span>{EMOTION_ICONS[s.emotion]}</span>
                <span className="text-slate-300 text-sm">{s.emotion}</span>
              </div>
              <span className={`text-xs font-semibold ${LEVEL_COLORS[s.level]}`}>{s.level} ({s.percentage}%)</span>
            </div>
          ))}
          {result.emotionScores.every(s => s.level === 'Low') && (
            <p className="text-emerald-400 text-xs">No significant emotion signals from questionnaire</p>
          )}
        </div>
      </div>

      {/* Priority Actions */}
      {(confirmed.length > 0 || situational.length > 0) && (
        <div className="rounded-2xl p-5 bg-slate-900/60 border border-slate-700/50">
          <p className="text-slate-500 text-xs uppercase tracking-widest mb-3">Practitioner Priority Actions</p>
          <div className="space-y-3">
            {confirmed.map(ref => (
              <div key={`pa-${ref.emotion}`} className="flex gap-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                <span className="text-red-400 flex-shrink-0 mt-0.5">🔴</span>
                <div>
                  <p className="text-red-300 text-sm font-semibold">{ref.emotion} — CONFIRMED Core Pattern</p>
                  <p className="text-slate-400 text-xs mt-0.5">Requires energy regulation and inborn design awareness work. Discipline alone will not resolve this.</p>
                </div>
              </div>
            ))}
            {situational.map(ref => (
              <div key={`pa-sit-${ref.emotion}`} className="flex gap-3 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <span className="text-blue-400 flex-shrink-0 mt-0.5">🔵</span>
                <div>
                  <p className="text-blue-300 text-sm font-semibold">{ref.emotion} — SITUATIONAL</p>
                  <p className="text-slate-400 text-xs mt-0.5">Not from inborn design. Addressable with structured disciplinary measures and habit change.</p>
                </div>
              </div>
            ))}
            {dormant.map(ref => (
              <div key={`pa-dor-${ref.emotion}`} className="flex gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <span className="text-amber-400 flex-shrink-0 mt-0.5">🟡</span>
                <div>
                  <p className="text-amber-300 text-sm font-semibold">{ref.emotion} — DORMANT</p>
                  <p className="text-slate-400 text-xs mt-0.5">Pattern exists in design but not currently active. Monitor under stress conditions.</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
